export interface ParsedMessagePart {
  type: string;
  text: string;
}

/**
 * Recursively parses message content that may be JSON-encoded (potentially multiple times).
 * Handles various formats:
 * - Direct parts array: [{ type: "text", text: "..." }]
 * - Object with parts property: { parts: [...] }
 * - Nested JSON strings (double/triple encoded)
 * - Plain text strings
 * - Primitives (numbers, booleans, null) - returns original string
 * 
 * @param content - The message content string to parse
 * @param originalContent - The original unparsed content (used for primitives)
 * @param depth - Current recursion depth to prevent infinite loops
 * @returns Array of message parts or empty array if content is empty
 */
export const parseMessageContent = (
  content: string,
  originalContent = content,
  depth = 0
): ParsedMessagePart[] => {
  if (depth > 5) {
    return [
      {
        type: "text",
        text: content
      }
    ];
  }

  try {
    const parsed = JSON.parse(content);

    if (Array.isArray(parsed)) {
      return parsed;
    }

    if (parsed && typeof parsed === "object" && "parts" in parsed) {
      return parsed.parts;
    }

    if (typeof parsed === "string") {
      return parseMessageContent(parsed, originalContent, depth + 1);
    }

    if (typeof parsed !== "object" || parsed === null) {
      if (!originalContent || originalContent.trim().length === 0) {
        return [];
      }
      return [
        {
          type: "text",
          text: originalContent
        }
      ];
    }

    const textContent = parsed.content || parsed.text;
    if (!textContent || textContent.trim().length === 0) {
      return [];
    }
    return [
      {
        type: "text",
        text: textContent
      }
    ];
  } catch {
    if (!content || content.trim().length === 0) {
      return [];
    }
    return [
      {
        type: "text",
        text: content
      }
    ];
  }
};

